/**
 * An arrangement said in one line: who it is for, how much of the score is in it, and what in
 * it is not the score's.
 *
 * It is what Arrange says over the page, to somebody who gave a score and named its players
 * and set nothing else. So it counts notes and players and uses no word of the lab's: nothing
 * of faults, starts or add-ons, which are behind Details for whoever wants them. Every figure
 * is the run's own, and a sentence is left out where its figure is nought.
 */

import { ENSEMBLES, type ArrangePayload, type KeptPayload, type Seat } from "@app/shared-types";
import { seatLabel } from "../components/ArrangeRoster";
import { plural, sentence } from "./arrange-words";
import { count } from "./format";
import { tally } from "./roster";

/** "is" for one and "are" for any other count. */
function are(amount: number, one = "is", many = "are"): string {
  return amount === 1 ? one : many;
}

/** Names in a row, the last joined by "and". */
function listed(names: readonly string[]): string {
  if (names.length < 3) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Who `roster` is, as the line begins: an ensemble by its name, or its players counted. */
export function forWhom(roster: readonly string[], seats: readonly Seat[]): string {
  // The order players were added in is not the ensemble's own, so it is matched by its count
  // of each instrument.
  const counted = tally(roster);
  const preset = ENSEMBLES.find((one) => {
    const its = tally(one.roster);
    return (
      one.roster.length === roster.length &&
      its.size === counted.size &&
      [...its].every(([instrument, many]) => counted.get(instrument) === many)
    );
  });
  if (preset !== undefined && roster.length > 1) return `a ${preset.name.toLowerCase()}`;
  // Counted as the chips count them, since an instrument's name has no one plural to trust.
  const names = [...counted].map(([instrument, many]) => {
    const name = seatLabel(instrument, seats).toLowerCase();
    return many === 1 ? name : `${name} ×${count(many)}`;
  });
  return roster.length === 1 ? `one ${names[0]}` : listed(names);
}

/** What of the score is in a run that hands the players its own notes, and what is added. */
function kept(result: KeptPayload): string[] {
  const { read } = result.source;
  const out = read - result.written;
  const said = [
    out === 0
      ? `all ${count(read)} of the score's notes are played.`
      : `${count(result.written)} of the score's ${count(read)} notes are played and ${count(out)} left out.`,
  ];
  const own = result.added + result.countered + result.varied;
  if (own > 0) {
    said.push(`${plural(own, "note")} ${are(own)} added that the score does not have.`);
  }
  return said;
}

/** What of the score is in a run the note model wrote under the tune. */
function under(result: KeptPayload): string[] {
  return [
    `the tune is the score's own ${plural(result.written, "note")}, and the ${plural(result.added, "note")} under it ${are(result.added)} newly written, in place of the score's ${count(result.left.replaced)}.`,
  ];
}

/** The arrangement `result`, written for `roster`, in a line. */
export function arrangeSaid(
  result: ArrangePayload,
  roster: readonly string[],
  seats: readonly Seat[],
): string {
  const said: string[] = [];
  if (result.start === "tune") {
    said.push("the tune is kept as the score has it, and every other part is written anew.");
    if (result.unwritten !== null) {
      said.push(`The note model wrote nothing, so the rules arranged it: ${result.unwritten}.`);
    }
    const bare = result.total - result.placed;
    if (bare > 0) {
      said.push(
        `${count(bare)} of its ${count(result.total)} chords found no voicing and ${are(bare)} left to the tune.`,
      );
    }
  } else {
    said.push(...(result.under === null ? kept(result) : under(result)));
    const idle = result.staves.filter((staff) => staff.plays === "rest").length;
    if (idle > 0) {
      said.push(
        `${count(idle)} of the ${count(roster.length)} players ${are(idle, "has", "have")} nothing to play.`,
      );
    }
    const awkward = result.idiom.length;
    if (awkward > 0) {
      said.push(
        `${plural(awkward, "thing")} a player may find awkward ${are(awkward)} listed under Details.`,
      );
    }
  }
  if (result.key !== null) said.push(`${result.key.said}.`);
  if (result.tempo !== null) said.push(`${result.tempo.said}.`);
  if (result.start !== "tune") said.push(...result.wished.map((one) => `${one}.`));
  const [first = "", ...rest] = said;
  const line = `For ${forWhom(roster, seats)}: ${first}`;
  return [line, ...rest.map(sentence)].join(" ");
}
